import { cn } from '@workspace/ui/lib/utils'
import type { ReactNode } from 'react'

export type StatusTone = 'live' | 'paused' | 'bad'

export interface StatusRowProps {
  label: ReactNode
  /** Right-aligned supporting text — a timestamp, a cadence, a reason. */
  detail?: ReactNode
  tone: StatusTone
  className?: string
}

const DOT_CLASS: Record<StatusTone, string> = {
  live: 'bg-emerald-400',
  paused: 'bg-yellow-500',
  bad: 'bg-red-400',
}

/**
 * The one-line "is this thing running" header for a campaign card: a status
 * dot, what it is doing, and a quiet detail on the right.
 *
 * Three tones rather than a boolean, because paused-by-the-founder and
 * broken-on-our-side are different situations and must never look alike.
 */
export function StatusRow({ label, detail, tone, className }: StatusRowProps) {
  return (
    <div className={cn('flex items-center gap-2.5 text-[13px] text-foreground', className)}>
      <span className={cn('size-2 shrink-0 rounded-full', DOT_CLASS[tone])} aria-hidden />
      <span className="font-medium">{label}</span>
      {detail && <span className="ml-auto text-[12px] text-muted-foreground">{detail}</span>}
    </div>
  )
}
